import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  eachHourOfInterval,
  subHours,
  addHours,
  subMinutes
} from 'date-fns/esm';
import {
  onWebsocketMessageDispatchUpdateEntities,
  onWebsocketMessageDispatchAddRemoveEntities,
  MergeStrategy,
  EntityOps,
  isEntityOp,
  errorOf, selectEntityList
} from '@zerops/zef/entities';
import { ZefSnackService } from '@zerops/zef/snack';
import { normalizeApiUrl } from '@zerops/zef/utils';
import { select, Store } from '@ngrx/store';
import { ServiceStackStatuses, ServiceStack } from '@zerops/models/service-stack';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { ProcessEntity } from '@zerops/zerops/core/process-base';
import { StatisticsEntities, StatisticsGroupBy } from '@zerops/models/resource-statistics';
import { deletionWarningDialogOpen } from '@zerops/zerops/feature/deletion-warning-dialog';
import { loadCurrentStatistics } from '@zerops/zerops/core/resource-statistics-base';
import { transactionDebitGroupPrefillData } from '@zerops/zerops/core/transaction-debit-base';
import {
  TransactionDebitGroupItem,
  TransactionGroupBy,
  TRANSACTION_GROUP_RANGE
} from '@zerops/models/transaction-debit';
import difference from 'lodash-es/difference';
import {
  ImportExportDialogModes,
  importExportDialogOpen,
  IMPORT_EXPORT_DIALOG_FEATURE_NAME
} from '@zerops/zerops/feature/import-export-dialog';
import { zefDialogClose } from '@zerops/zef/dialog';
// explicit import due to cyclic dependency problems
import { ApiEntityKeys, AppState } from '@zerops/zerops/app';
import {
  switchMap,
  map,
  catchError,
  filter,
  mergeMap,
  withLatestFrom,
  pairwise,
  tap
} from 'rxjs/operators';
import { of } from 'rxjs';
import {
  addServiceStack,
  addServiceStackFail,
  addServiceStackSuccess,
  serviceStackStart,
  serviceStackStop,
  serviceStackRestart,
  serviceStackReload,
  serviceStackStartSuccess,
  serviceStackStopSuccess,
  serviceStackRestartSuccess,
  serviceStackReloadSuccess,
  serviceStackStartFail,
  serviceStackStopFail,
  serviceStackRestartFail,
  serviceStackReloadFail,
  enableSubdomainAccess,
  enableSubdomainAccessSuccess,
  enableSubdomainAccessFail,
  disableSubdomainAccess,
  disableSubdomainAccessSuccess,
  disableSubdomainAccessFail,
  serviceStackModifyInternalPorts,
  serviceStackModifyInternalPortsSuccess,
  serviceStackModifyInternalPortsFail,
  integrateExternalRepository,
  integrateExternalRepositorySuccess,
  integrateExternalRepositoryFail,
  removeExternalRepositoryIntegration,
  removeExternalRepositoryIntegrationSuccess,
  removeExternalRepositoryIntegrationFail,
  updateObjectStorageSettings,
  updateObjectStorageSettingsSuccess,
  updateObjectStorageSettingsFail,
  updateServiceStack,
  updateServiceStackFail,
  updateServiceStackSuccess,
  serviceStackExport,
  serviceStackExportSuccess,
  serviceStackExportFail,
  serviceStackImport,
  serviceStackImportSuccess,
  serviceStackImportFail,
  firstClassRecipeImport,
  firstClassRecipeImportSuccess,
  firstClassRecipeImportFail,
  adjustServiceStackAutoscaling,
  adjustServiceStackAutoscalingSuccess,
  adjustServiceStackAutoscalingFail,
  deleteBuildCache,
  deleteBuildCacheSuccess,
  deleteBuildCacheFail,
  deleteRuntimePrepareCache,
  deleteRuntimePrepareCacheSuccess,
  deleteRuntimePrepareCacheFail,
  getContainerFile,
  getContainerFileSuccess,
  getContainerFileFail,
  getBackups,
  getBackupsSuccess,
  getBackupsFail,
  getProjectBackups,
  getProjectBackupsSuccess,
  getProjectBackupsFail,
  createBackup,
  createBackupSuccess,
  createBackupFail,
  setBackupConfig,
  deleteBackup,
  deleteBackupSuccess,
  deleteBackupFail,
  setBackupConfigSuccess,
  setBackupConfigFail,
  pipelineDebug,
  pipelineDebugFail,
  pipelineDebugSuccess,
  triggerRedeploy,
  triggerRedeploySuccess,
  triggerRedeployFail,
  triggerPipeline,
  triggerPipelineSuccess,
  triggerPipelineFail,
  backupDownloadUrl,
  backupDownloadUrlFail,
  backupDownloadUrlSuccess,
  copyBackupDownloadLink,
  copyBackupDownloadLinkSuccess,
  copyBackupDownloadLinkFail
} from './service-stack-base.action';
import { ServiceStackBaseApi } from './service-stack-base.api';
import { ServiceStackEntity } from './service-stack-base.entity';
import { selectServiceStackBaseState } from './service-stack-base.selector';
import {
  FEATURE_NAME,
  PipelineDebugPhases,
  PipelineDebugPhasesUserDataKeys,
  PROJECT_BACKUPS_PAGE_SIZE,
  ServiceStackOperationTypes
} from './service-stack-base.constant';
import { UserDataEntity } from '../user-data-base';
import { AUTOSCALING_UPDATE_DIALOG } from '@zerops/zerops/feature/autoscaling-update-dialog';
import { Clipboard } from '@angular/cdk/clipboard';

@Injectable()
export class ServiceStackBaseEffect {

  private _clientId$ = this._userEntity.activeClientId$.pipe(filter((d) => !!d));

  private _setupUpdateStreamSubscription$ = createEffect(() => this._clientId$.pipe(
    map((clientId) => this._serviceStackEntity.updateSubscribe(clientId)
  )));

  private _setupListStreamSubscription$ = createEffect(() => this._clientId$.pipe(
    map((clientId) => this._serviceStackEntity.listSubscribe(
      clientId,
      FEATURE_NAME,
      undefined,
      {
        handleGlobally: false
      }
    ))
  ));

  private _setupAddRemoveMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(this._serviceStackEntity)
  ));


  private _prefillTransactionDebitForProjects$ = createEffect(() => this._store.pipe(
    // we only need ids, so we are taking them raw from the state
    select(selectEntityList(this._serviceStackEntity.entityName)),
    pairwise(),
    // checking if there are new ids
    map(([ prevIds, currIds ]) => difference(currIds, prevIds)),
    mergeMap((ids: string[]) => {

      const groupRange = TRANSACTION_GROUP_RANGE.last24h;

      const items = ids.reduce((arr: any, id: string) => {

        arr.push(...eachHourOfInterval({
          start: addHours(groupRange.range.from, 1),
          end: groupRange.range.to
        }).map((itm: Date) => ({
          sumTotalPrice: 0,
          from: subHours(itm, 1),
          stackId: id,
          cpu: 0,
          disc: 0,
          ram: 0,
          till: subMinutes(itm, 1)
        })));

        return arr;

      }, []) as TransactionDebitGroupItem[];

      return [
        transactionDebitGroupPrefillData({
          timeGroupBy: groupRange.timeGroupBy,
          groupBy: TransactionGroupBy.Metric,
          limit: groupRange.limit,
          from: groupRange.range.from,
          till: groupRange.range.to,
          key: groupRange.key,
          items
        })
      ];

    })
  ));

  private _onAddServiceStackReloadCurrentStatistics$ = createEffect(() => this._actions$.pipe(
    filter((action) => isEntityOp(this._serviceStackEntity.entityName, EntityOps.UpdateCacheDone, action)),
    // TODO: action interface
    // get action payload
    map((action: any): { result: string[]; entities: any; } => action.data?.items),
    // check if there were any updated entities
    filter((d) => !!d?.result?.length),
    // find those entities whom status is active or creating
    map((d) => d.result
      .reduce((arr, id) => {
        arr.push(d.entities[ApiEntityKeys.ServiceStack][id]);
        return arr;
      }, [])
    ),
    // gather previous value
    pairwise(),
    // return any entities that changed to active or stopped
    map(([ prev, next ]) => {
      const prevHashMap = prev.reduce((obj, itm) => {
        obj[itm.id] = itm.status;
        return obj;
      }, {});

      return next?.filter((itm) => (itm.status === ServiceStackStatuses.Active
        && prevHashMap[itm.id] !== ServiceStackStatuses.Active)
        || (itm.status === ServiceStackStatuses.Stopped
          && prevHashMap[itm.id] !== ServiceStackStatuses.Stopped))
      || [];
    }),
    filter((d) => !!d?.length),
    withLatestFrom(this._clientId$),
    map(([ _, clientId ]) => loadCurrentStatistics({
      data: {
        clientId,
        groupBy: StatisticsGroupBy.Service,
        statisticsEntity: StatisticsEntities.ServiceStack
      }
    }))
  ));

  private _onUpdateStreamMessage$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(
      this._serviceStackEntity,
      {
        zefEntityMergeStrategy: {
          ports: MergeStrategy.KeepNew
        }
      }
    )
  ));

  private _onAddServiceStack$ = createEffect(() => this._actions$.pipe(
    ofType(addServiceStack),
    mergeMap((action) => this._api
      .add$(action.data.projectId, action.data.serviceVersion, action.data.serviceData)
      .pipe(
        map((res) => addServiceStackSuccess(res, action)),
        catchError((res) => of(addServiceStackFail(res, action)))
      )
    )
  ));

  private _onAddServiceStackSuccess$ = this._actions$.pipe(ofType(addServiceStackSuccess));

  private _onAddServiceStackUpdateCache = createEffect(() => this._onAddServiceStackSuccess$.pipe(
    map((d) => this._serviceStackEntity.addToCache([ d.data ]))
  ));

  // -- open deploy settings dialog after adding a new user service
  // private _onAddServiceStackSuccessOpenNewServiceDialog$ = createEffect(() => this._onAddServiceStackSuccess$.pipe(
  //   filter((d) => d?.data?.status === ServiceStackStatuses.New
  //     && d?.data?.serviceStackTypeInfo.serviceStackTypeCategory === ServiceStackTypeCategories.User
  //   ),
  //   map(({ data }) => newServiceDialogOpen(data)),
  // ));

  // -- start, stop, restart, reload
  private _onTriggerServiceStackOperation$ = createEffect(() => this._actions$.pipe(
    ofType(
      serviceStackStart,
      serviceStackStop,
      serviceStackRestart,
      serviceStackReload
    ),
    mergeMap((action) => this._api
      .triggerServiceStackOperation$(action.data.id, action.data.type)
      .pipe(
        map((response) => {
          switch (action.data.type) {
            case ServiceStackOperationTypes.Start: return serviceStackStartSuccess(response, action);
            case ServiceStackOperationTypes.Stop: return serviceStackStopSuccess(response, action);
            case ServiceStackOperationTypes.Restart: return serviceStackRestartSuccess(response, action);
            case ServiceStackOperationTypes.Reload: return serviceStackReloadSuccess(response, action);
          }
        }),
        catchError((err) => {
          switch (action.data.type) {
            case ServiceStackOperationTypes.Start: return of(serviceStackStartFail(err, action));
            case ServiceStackOperationTypes.Stop: return of(serviceStackStopFail(err, action));
            case ServiceStackOperationTypes.Restart: return of(serviceStackRestartFail(err, action));
            case ServiceStackOperationTypes.Reload: return of(serviceStackReloadFail(err, action));
          }
        })
      )
    )
  ));

  // -- get a container file, as the Nginx config, through the file browser api
  onGetContainerFile$ = createEffect(() => this._actions$.pipe(
    ofType(getContainerFile),
    switchMap((action) => this._api
      .getContainerFile$(action.data.serviceStackId, action.data.containerId, action.data.filePath)
      .pipe(
        map((response) => getContainerFileSuccess(
          window.atob(response.content),
          action
        )),
        catchError((err) => of(getContainerFileFail(err, action)))
      )
    )
  ));

  // -- enable subdomain access
  private _enableSubdomainAccess$ = createEffect(() => this._actions$.pipe(
    ofType(enableSubdomainAccess),
    switchMap((action) => this._api
      .enableSubdomainAccess$(action.data.id)
      .pipe(
        map((response) => enableSubdomainAccessSuccess(
          response,
          action
        )),
        catchError((err) => of(enableSubdomainAccessFail(err, action)))
      )
    )
  ));

  // -- disable subdomain access
  private _disableSubdomainAccess$ = createEffect(() => this._actions$.pipe(
    ofType(disableSubdomainAccess),
    switchMap((action) => this._api
      .disableSubdomainAccess$(action.data.id)
      .pipe(
        map((response) => disableSubdomainAccessSuccess(
          response,
          action
        )),
        catchError((err) => of(disableSubdomainAccessFail(err, action)))
      )
    )
  ));

  // -- modify internal ports
  private _onServiceStackModifyInternalPorts$ = createEffect(() => this._actions$.pipe(
    ofType(serviceStackModifyInternalPorts),
    switchMap((action) => this._api
      .modifyInternalPorts$(action.data.serviceStackId, action.data.ports)
      .pipe(
        map((response) => serviceStackModifyInternalPortsSuccess(response, action)),
        catchError((err) => of(serviceStackModifyInternalPortsFail(err, action)))
      )
    )
  ));

  // -- update object storage size
  private _onUpdateObjectStorageSize$ = createEffect(() => this._actions$.pipe(
    ofType(updateObjectStorageSettings),
    switchMap((action) => this._api
      .updateObjectStorageSettings$(
        action.data.id,
        action.data.diskGBytes,
        action.data.policy,
        action.data.rawPolicy,
        action.data.cdnEnabled
      )
      .pipe(
        map((response) => updateObjectStorageSettingsSuccess(response, action)),
        catchError((err) => of(updateObjectStorageSettingsFail(err, action)))
      )
    )
  ));

  // -- external repository integration
  private _onIntegrateExternalRepository$ = createEffect(() => this._actions$.pipe(
    ofType(integrateExternalRepository),
    switchMap((action) => this._api
      .externalRepositoryIntegration$(action.data.id, action.data)
      .pipe(
        map((response) => integrateExternalRepositorySuccess(response, action)),
        catchError((err) => of(integrateExternalRepositoryFail(err, action)))
      )
    )
  ));

  // -- remove external repository integration
  private _onRemoveExternalRepositoryIntegration$ = createEffect(() => this._actions$.pipe(
    ofType(removeExternalRepositoryIntegration),
    switchMap((action) => this._api
      .externalRepositoryIntegration$(action.data.id, {
        gitLabIntegration: null,
        gitHubIntegration: null
      })
      .pipe(
        map((response) => removeExternalRepositoryIntegrationSuccess(response, action)),
        catchError((err) => of(removeExternalRepositoryIntegrationFail(err, action)))
      )
    )
  ));

  private _onGetOneError$ = createEffect(() => this._actions$.pipe(
    errorOf<ServiceStack>(this._serviceStackEntity.getOne),
    filter(({ meta: { zefError } }) => zefError.code === 'serviceStackNotFound'),
    map(() => deletionWarningDialogOpen({ entity: ApiEntityKeys.ServiceStack }))
  ));

  // -- stack update
  private _onUpdateServiceStack$ = createEffect(() => this._actions$.pipe(
    ofType(updateServiceStack),
    switchMap((action) => this._api
      .stackUpdate$(action.data?.id, action.data?.typeVersion, action.data?.data)
      .pipe(
        map((res) => updateServiceStackSuccess(res, action)),
        catchError((err) => of(updateServiceStackFail(err, action)))
      )
    )
  ));

  private _onServiceStackExport$ = createEffect(() => this._actions$.pipe(
    ofType(serviceStackExport),
    switchMap((action) => this._api
      .serviceStackExport$(action.data.id, action.data.reveal)
      .pipe(
        map((res) => serviceStackExportSuccess(res.yaml, action)),
        catchError((res) => of(serviceStackExportFail(res, action)))
      )
    )
  ));

  private _onServiceStackExportSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(serviceStackExportSuccess),
    map(({ data, originalAction }) => importExportDialogOpen({
      yaml: data,
      mode: ImportExportDialogModes.Export,
      entity: 'service-stack',
      exportId: originalAction?.data?.id,
      reveal: !!originalAction?.data?.reveal,
      canRevealSecrets: !!originalAction?.data?.canRevealSecrets
    }))
  ));

  private _onServiceStackImport$ = createEffect(() => this._actions$.pipe(
    ofType(serviceStackImport),
    switchMap((action) => this._api
      .serviceStackImport$(
        action.data.yaml,
        action.data.projectId,
        action.data.recipeSource,
        action.data.recipeSourceUrl
      )
      .pipe(
        map((response) => serviceStackImportSuccess(response, action)),
        catchError((res) => of(serviceStackImportFail(res, action)))
      )
    )
  ));

  private _onServiceStackImportSuccess$ = this._actions$.pipe(
    ofType(serviceStackImportSuccess)
  );

  private _onServiceStackImportSuccessNotification$ = createEffect(() => this._onServiceStackImportSuccess$.pipe(
    switchMap(() => this._snack.success$({ text: 'Service was imported successfully' }))
  ), { dispatch: false });

  private _onServiceStackImportSuccessDialogClose$ = createEffect(() => this._onServiceStackImportSuccess$.pipe(
    map(() => zefDialogClose({
      key: IMPORT_EXPORT_DIALOG_FEATURE_NAME
    }))
  ));

  // -- first-class recipe import (development containers)
  private _onFirstClassRecipeImport$ = createEffect(() => this._actions$.pipe(
    ofType(firstClassRecipeImport),
    switchMap((action) => this._api
      .firstClassRecipeImport$(
        action.data.projectId,
        action.data.serviceImportYaml,
        action.data.recipeSource,
        action.data.recipeSourceUrl,
        action.data.createIntegrationToken
      )
      .pipe(
        map((response) => firstClassRecipeImportSuccess(response, action)),
        catchError((res) => of(firstClassRecipeImportFail(res, action)))
      )
    )
  ));

  private _onFirstClassRecipeImportSuccess$ = this._actions$.pipe(
    ofType(firstClassRecipeImportSuccess)
  );

  private _onFirstClassRecipeImportSuccessNotification$ = createEffect(() => this._onFirstClassRecipeImportSuccess$.pipe(
    filter((action) => !action.originalAction?.meta?.skipNotification),
    switchMap(() => this._snack.success$({ text: 'Development container deployed successfully' }))
  ), { dispatch: false });

  /**
   * Seed the new project's service stacks into the entity store directly
   * from the import response. The backend attaches the created stacks to
   * each `Process.serviceStacks`; without this seeding step the project
   * detail page renders its empty-state until the websocket finally
   * delivers them — visible lag for fresh ZCP / zagent creates that
   * looks like "the deploy didn't do anything". Adding from the API
   * response makes the UI flip the moment the request settles, and the
   * subsequent WS push just replaces with the same data on the next
   * tick.
   */
  private _onFirstClassRecipeImportSuccessSeedStacks$ = createEffect(() => this._onFirstClassRecipeImportSuccess$.pipe(
    map((action) => {
      const stackMap = new Map<string, ServiceStack>();
      for (const p of action.data.processes ?? []) {
        for (const s of p.serviceStacks ?? []) {
          if (s?.id) stackMap.set(s.id, s);
        }
      }
      return Array.from(stackMap.values());
    }),
    filter((stacks) => stacks.length > 0),
    map((stacks) => this._serviceStackEntity.addToCache(stacks))
  ));

  /**
   * Companion seed for the *processes* in the import response. The same
   * `action.data.processes` payload that we mined for stacks above also
   * IS the queued/running build+deploy work; without this seed the
   * project-detail page sees `processEntity.list$() === []` for the
   * ~500ms window before the global process listSubscribe roundtrips,
   * so the empty-state ("Add your first service") flashes in instead
   * of the actual deploy panel that uses these processes.
   */
  private _onFirstClassRecipeImportSuccessSeedProcesses$ = createEffect(() => this._onFirstClassRecipeImportSuccess$.pipe(
    map((action) => (action.data.processes ?? []).filter((p) => !!p?.id)),
    filter((processes) => processes.length > 0),
    map((processes) => this._processEntity.addToCache(processes))
  ));

  private _onAdjustServiceStackAutoscaling$ = createEffect(() => this._actions$.pipe(
    ofType(adjustServiceStackAutoscaling),
    switchMap((action) => this._api
      .adjustAutoscaling$(
        action.data.serviceStackId,
        action.data.customAutoscaling,
        action.data.autoscalingProfileId,
        action.data.autoscalingProfileOverrides
      )
      .pipe(
        map((res) => adjustServiceStackAutoscalingSuccess(res, action)),
        catchError((res) => of(adjustServiceStackAutoscalingFail(res, action)))
      )
    )
  ));

  // TODO ?
  private _onAdjustServiceStackAutoscalingSuccessCloseDialog$ = createEffect(() => this._actions$.pipe(
    ofType(adjustServiceStackAutoscalingSuccess),
    map(() => zefDialogClose({ key: AUTOSCALING_UPDATE_DIALOG }))
  ));

  // -- clear build cache
  private _onDeleteBuildCache$ = createEffect(() => this._actions$.pipe(
    ofType(deleteBuildCache),
    switchMap((action) => this._api
      .deleteBuildCache$(action.data.id).pipe(
        map(() => deleteBuildCacheSuccess(undefined, action)),
        catchError((res) => of(deleteBuildCacheFail(res, action)))
      )
    )
  ));

  // -- clear runtime prepare cache
  private _onDeleteRuntimePrepareCache$ = createEffect(() => this._actions$.pipe(
    ofType(deleteRuntimePrepareCache),
    switchMap((action) => this._api
      .deleteRuntimePrepareCache$(action.data.id).pipe(
        map(() => deleteRuntimePrepareCacheSuccess(undefined, action)),
        catchError((res) => of(deleteRuntimePrepareCacheFail(res, action)))
      )
    )
  ));

  private _onDeleteBuildCacheSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(deleteBuildCacheSuccess),
    switchMap(() => this._snack.success$({ text: 'Build cache invalidated successfully' }))
  ), { dispatch: false });

  private _onDeleteRuntimePrepareCacheSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(deleteRuntimePrepareCacheSuccess),
    switchMap(() => this._snack.success$({ text: 'Runtime prepare cache invalidated successfully' }))
  ), { dispatch: false });

  // -- get backups
  private _onGetBackups$ = createEffect(() => this._actions$.pipe(
    ofType(getBackups),
    switchMap((action) => this._api
      .getBackups$(action.data.id).pipe(
        map((res) => getBackupsSuccess(res, action)),
        catchError((res) => of(getBackupsFail(res, action)))
      )
    )
  ));

  // -- get project backups
  private _onGetProjectBackups$ = createEffect(() => this._actions$.pipe(
    ofType(getProjectBackups),
    switchMap((action) => this._api
      .getProjectBackups$(action.data.projectId, {
        limit: PROJECT_BACKUPS_PAGE_SIZE,
        offset: action.data.offset || 0,
        sort: action.data.sort || [ '-created' ]
      }).pipe(
        map((res) => getProjectBackupsSuccess(res, action)),
        catchError((res) => of(getProjectBackupsFail(res, action)))
      )
    )
  ));

  // -- create backup
  private _onCreateBackup$ = createEffect(() => this._actions$.pipe(
    ofType(createBackup),
    switchMap((action) => this._api
      .createBackup$(action.data.id, action.data.tags).pipe(
        map((res) => createBackupSuccess(res, action)),
        catchError((res) => of(createBackupFail(res, action)))
      )
    )
  ));

  private _onCreateBackupSuccessReloadList$ = createEffect(() => this._actions$.pipe(
    ofType(createBackupSuccess),
    map(({ originalAction }) => getBackups({ id: originalAction.data.id }))
  ));

  private _onCreateBackupSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(createBackupSuccess),
    switchMap(({ data }) => this._snack.success$({
      text: data?.async
        ? 'Backup is running on background, try refreshing the page later..'
        : 'Backup successfully created, refreshing list..'
    }, {
      duration: data?.async ? 2500 : 1500
    }))
  ), { dispatch: false });

  // -- set backup frequency
  private _onsetBackupConfig$ = createEffect(() => this._actions$.pipe(
    ofType(setBackupConfig),
    switchMap((action) => this._api
      .setBackupConfig$(action.data.serviceId, action.data.config).pipe(
        map((res) => setBackupConfigSuccess(res.process, action)),
        catchError((res) => of(setBackupConfigFail(res, action)))
      )
    )
  ));

  // -- download backup
  onDownloadUrlBackup$ = createEffect(() => this._actions$.pipe(
    ofType(backupDownloadUrl),
    switchMap((action) => this._api
      .downloadUrlBackup$(action.data.projectId, action.data.serviceId, action.data.id).pipe(
        tap(({ url }) => window.location.replace(normalizeApiUrl(url))),
        map(() => backupDownloadUrlSuccess(action.data, action)),
        catchError((res) => of(backupDownloadUrlFail(res, action)))
      )
    )
  ));

  onDownloadUrlBackupSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(backupDownloadUrlSuccess),
    switchMap(({ data }) => this._snack.success$({
      text: `Backup ${data.filename} has been successfully downloaded`
    }, {
      duration: 3000
    }))
  ), { dispatch: false });

  // -- copy download link
  onCopyBackupDownloadLink$ = createEffect(() => this._actions$.pipe(
    ofType(copyBackupDownloadLink),
    switchMap((action) =>
      this._api.downloadUrlBackup$(action.data.projectId, action.data.serviceId, action.data.name).pipe(
        tap(({ url }) => this._clipboard.copy(normalizeApiUrl(url))),
        map(() => copyBackupDownloadLinkSuccess(action.data, action)),
        catchError((res) => of(copyBackupDownloadLinkFail(res, action)))
      )
    )
  ));

  onCopyBackupDownloadLinkSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(copyBackupDownloadLinkSuccess),
    switchMap(() => this._snack.success$({ text: 'Link copied to clipboard.' }, { duration: 800 }))
  ), { dispatch: false });

  // -- delete backup
  private _onDeleteBackup$ = createEffect(() => this._actions$.pipe(
    ofType(deleteBackup),
    switchMap((action) => this._api
      .deleteBackup$(action.data.projectId, action.data.serviceId, action.data.id).pipe(
        map(() => deleteBackupSuccess(action.data, action)),
        catchError((res) => of(deleteBackupFail(res, action)))
      )
    )
  ));

  // the project-level list is server-sorted and paginated, so re-fetch it instead of patching it locally,
  // but only when it has been loaded for that project already (the stack page alone never needs it).
  // Stays on the current page and steps back one when the deleted item was the last one on it.
  private _onDeleteBackupSuccessReloadProjectBackups$ = createEffect(() => this._actions$.pipe(
    ofType(deleteBackupSuccess),
    withLatestFrom(this._store.pipe(select(selectServiceStackBaseState))),
    filter(([ { data }, state ]) => !!state?.projectBackups?.[data.projectId]),
    map(([ { data }, state ]) => {
      const { sort, offset, list } = state.projectBackups[data.projectId];
      const wasLastOnPage = list?.length <= 1 && offset > 0;
      return getProjectBackups({
        projectId: data.projectId,
        sort,
        offset: wasLastOnPage ? Math.max(offset - PROJECT_BACKUPS_PAGE_SIZE, 0) : offset
      });
    })
  ));

  private _onDeleteBackupSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(deleteBackupSuccess),
    switchMap(() => this._snack.success$({
      text: 'Backup successfully deleted'
    }, {
      duration: 1500
    }))
  ), { dispatch: false });

  // -- pipeline debug
  private _onSetPipelineDebug$ = createEffect(() => this._actions$.pipe(
    ofType(pipelineDebug),
    switchMap((action) => this._api
      .pipelineDebugMode$(action.data.id, action.data.phase, action.data.mode).pipe(
        map((res) => pipelineDebugSuccess(res, action)),
        catchError((res) => of(pipelineDebugFail(res, action)))
      )
    )
  ));

  private _onPipelineDebugSuccessUpdateUserData$ = createEffect(() => this._actions$.pipe(
    ofType(pipelineDebugSuccess),
    withLatestFrom(this._userDataEntity.all$()),
    map(([ { data }, allUserData ]) => {
      const pipelineDebugData = allUserData
        ?.filter((itm) => itm.key === PipelineDebugPhasesUserDataKeys.DEBUG_BUILD
          || itm.key === PipelineDebugPhasesUserDataKeys.DEBUG_RUNTIME_PREPARE)
        .map((itm) => {
          if (itm.key === PipelineDebugPhasesUserDataKeys.DEBUG_BUILD) {
            return {
              ...itm,
              content: data[PipelineDebugPhases.DEBUG_BUILD]
            };
          }
          if (itm.key === PipelineDebugPhasesUserDataKeys.DEBUG_RUNTIME_PREPARE) {
            return {
              ...itm,
              content: data[PipelineDebugPhases.DEBUG_RUNTIME_PREPARE]
            };
          }
        });

      return this._userDataEntity.updateCache(pipelineDebugData || []);
    })
  ));

  // -- trigger re-deploy
  private _onTriggerRedeploy$ = createEffect(() => this._actions$.pipe(
    ofType(triggerRedeploy),
    switchMap((action) => this._api
      .triggerRedeploy$(action.data.id, action.data.envVarsFile, action.data.envSecretsFile).pipe(
        map((res) => triggerRedeploySuccess(res, action)),
        catchError((res) => of(triggerRedeployFail(res, action)))
      )
    )
  ));

  // -- trigger pipeline
  private _onTriggerPipeline$ = createEffect(() => this._actions$.pipe(
    ofType(triggerPipeline),
    switchMap((action) => this._api
      .triggerPipeline$(action.data.id, action.data.data).pipe(
        map((res) => triggerPipelineSuccess(res, action)),
        catchError((res) => of(triggerPipelineFail(res, action)))
      )
    )
  ));

  constructor(
    private _actions$: Actions,
    private _api: ServiceStackBaseApi,
    private _snack: ZefSnackService,
    private _store: Store<AppState>,
    private _serviceStackEntity: ServiceStackEntity,
    private _processEntity: ProcessEntity,
    private _userEntity: UserEntity,
    private _userDataEntity: UserDataEntity,
    private _clipboard: Clipboard
  ) { }

}
